import { useAppSelector } from '../../../store/store';
import { selectCustomers } from '../../../store/slices/customers';
import { useTaskTranslation } from '../hooks/useTaskTranslation';
import type { HTMLAttributes, Ref } from 'react';
import { TaskPriorityIndicator } from './TaskPriorityIndicator';
import styles from './TaskCard.module.css';
import categoryType from '../../../assets/icons/Check circle.svg';
import type { Task } from '../../../store/slices/tasks';
import { findTaskCustomer, taskAssigneeColor, taskInitials } from '../taskUtils';

type Props = {
  task: Task;
  variant?: 'kanban' | 'detailed';
  onClick?: () => void;
  dragProps?: HTMLAttributes<HTMLDivElement>;
  dragRef?: Ref<HTMLDivElement>;
};

export const TaskCard = ({ task, variant = 'kanban', onClick, dragProps, dragRef }: Props) => {
  const { translations, taskText } = useTaskTranslation();
  const customers = useAppSelector(selectCustomers);
  const customer = findTaskCustomer(task.customer_id, customers);
  const name = [customer?.person_firstname, customer?.person_lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const customerLabel = name
    ? `${name}${task.customer_id != null ? ` (#${task.customer_id})` : ''}`
    : task.customer_id != null
    ? `#${task.customer_id}`
    : '';
  const assignee = task.person_name_assign?.trim() || translations('task_board.unassigned');
  const assigneeColor = taskAssigneeColor(task.person_id_assign, task.person_name_assign);
  const taskId =
    task.task_id != null ? `ECO-${task.task_id}` : translations('task_board.no_task_id');

  return (
    <div
      {...dragProps}
      ref={dragRef}
      className={`${styles.card} ${styles[variant]} ${dragProps ? styles.draggable : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={taskText('task_board.open_task_id_title', {
        id: task.task_id ?? '',
        title: task.title || translations('task_board.untitled'),
      })}
      onKeyDown={(event) => {
        dragProps?.onKeyDown?.(event);
        if (event.defaultPrevented || (dragProps && event.key === ' ')) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      }}
    >
      {variant === 'detailed' ? (
        <div className={styles.gridContent}>
          <div className={styles.header}>
            <div
              className={styles.gridTitle}
              title={task.title || translations('task_board.untitled')}
            >
              {task.title || translations('task_board.untitled')}
            </div>
            <TaskPriorityIndicator priority={task.priority} />
          </div>
          {task.description?.trim() && (
            <p className={styles.gridDescription}>{task.description.trim()}</p>
          )}
          {customerLabel && (
            <div
              className={styles.gridCustomer}
              title={taskText('task_board.customer_name', { name: customerLabel })}
            >
              <span className={styles.gridCustomerLabel}>
                {translations('task_board.customer')}
              </span>{' '}
              {customerLabel}
            </div>
          )}
          <div className={styles.gridFooter}>
            <div
              className={styles.gridAssignee}
              title={taskText('task_board.assigned_to_name', { name: assignee })}
            >
              {task.person_name_assign?.trim() && (
                <div className={styles.gridAvatar} style={assigneeColor} aria-hidden="true">
                  {taskInitials(task.person_name_assign)}
                </div>
              )}
              <span className={styles.gridAssigneeName}>{assignee}</span>
            </div>
            <span className={styles.gridTaskId}>{taskId}</span>
          </div>
        </div>
      ) : (
        <div className={styles.cardLeft}>
          {task.person_name_assign?.trim() && (
            <div className={styles.avatar} style={assigneeColor} title={assignee}>
              {taskInitials(task.person_name_assign)}
            </div>
          )}
          <div className={styles.content}>
            <div className={styles.header}>
              <div className={styles.title}>
                {task.title || translations('task_board.untitled')}
              </div>
              <TaskPriorityIndicator priority={task.priority} />
            </div>
            <div className={styles.subtitle}>{assignee}</div>
            {customerLabel && (
              <div
                className={styles.customerName}
                title={taskText('task_board.customer_name', { name: customerLabel })}
              >
                {taskText('task_board.customer_name', { name: customerLabel })}
              </div>
            )}
            <div className={styles.infoRow}>
              <div className={styles.infoItems}>
                <div className={styles.infoItem}>
                  <img
                    src={categoryType}
                    className={styles.icon}
                    alt={translations('task_board.task_id')}
                  />
                  <span>{taskId}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
